// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Query binding for control-plane collections.
 *
 * Two properties matter and are implemented once:
 *
 * - **Tenant-scoped cache keys.** A cached result is a derivative that inherits
 *   its source's scope (`SEC2-016`, `DAT-006`), so the key always carries the
 *   scope the session resolved to.
 * - **Bounded retries.** An answer such as "denied" or "not found" is not a
 *   transient fault; retrying it adds load and hides the real state
 *   (`RUN-010`).
 */

import {
  ApiProblemError,
  useApiClients,
  useSession,
  type ControlPlaneClient,
} from '@eai/ui-shared';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

/** Maximum retry attempts for a genuinely transient failure. */
export const MAX_RETRIES = 2;

/** Retry only classes where repeating the identical request is safe. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiProblemError && !error.retryable) {
    return false;
  }
  return failureCount < MAX_RETRIES;
}

/**
 * Run a control-plane query under the current tenant scope.
 *
 * @param buildKey - Builds the cache key from the resolved tenant scope.
 * @param select - Chooses the client method to call.
 * @returns The query result.
 */
export function useControlPlaneQuery<TData>(
  buildKey: (scope: string) => readonly unknown[],
  select: (client: ControlPlaneClient, signal: AbortSignal) => Promise<TData>,
): UseQueryResult<TData, unknown> {
  const clients = useApiClients();
  const { scope } = useSession();

  return useQuery({
    queryKey: buildKey(scope),
    queryFn: ({ signal }) => select(clients.controlPlane, signal),
    retry: shouldRetry,
  });
}
